// Helpers for order totals.
// - subtotal   = items sum (excluding shipping and before discount)
// - shipping   = shipping charge
// - discount   = ₹ discount set by admin or store (default 0)
// - grandTotal = subtotal + shipping - discount

export const orderSubtotal = (o) => {
  if (!o) return 0;
  if (o.subtotal != null && o.subtotal !== "") return Number(o.subtotal);
  if (Array.isArray(o.items) && o.items.length > 0) {
    return o.items.reduce((s, i) => s + (Number(i.price || 0) * Number(i.quantity || 1)), 0);
  }
  const total = Number(o.total || 0);
  const shipping = Number(o.shippingCharge || 0);
  const discount = Number(o.discount || 0);
  return Math.max(0, total - shipping + discount);
};

export const orderShipping = (o, products = []) => {
  if (!o) return 0;
  if (o.shippingCharge != null && o.shippingCharge !== "") {
    return Number(o.shippingCharge);
  }
  if (Array.isArray(o.items) && o.items.length > 0) {
    return o.items.reduce((sum, it) => {
      const prod = Array.isArray(products) ? products.find((p) => p.id === it.id) : null;
      const shipUnit = it.shippingCharge != null && it.shippingCharge !== ""
        ? Number(it.shippingCharge)
        : Number(prod?.shippingCharge || 0);
      return sum + shipUnit * Number(it.quantity || 1);
    }, 0);
  }
  return 0;
};

export const invoiceShipping = (o, products = []) => {
  if (!o) return 0;
  if (o.deliveryIncluded !== false) return 0;
  return orderShipping(o, products);
};

export const orderDiscount = (o) => Number(o?.discount || 0);

export const orderExtraCharge = (o) => Number(o?.extraCharge || 0);

// COD charge is ₹50 or 3% of order amount, whichever is higher
export const calculateCodCharge = (amount) => {
  const amt = Number(amount || 0);
  if (amt <= 0) return 0;
  return Math.max(50, Math.round(amt * 0.03));
};

export const orderCodCharge = (o) => {
  if (!o) return 0;
  if (o.codCharge != null && o.codCharge !== "" && !isNaN(Number(o.codCharge))) {
    return Number(o.codCharge);
  }
  if (o.paymentMethod === "COD" || o.paymentType === "COD" || (typeof o.paymentMethod === "string" && o.paymentMethod.toUpperCase().includes("COD"))) {
    const billBeforeCod = orderSubtotal(o) + orderTaxTotal(o) + invoiceShipping(o) + orderExtraCharge(o) - orderDiscount(o);
    return calculateCodCharge(Math.max(0, billBeforeCod));
  }
  return 0;
};

export const orderTaxTotal = (o) => {
  if (!o) return 0;
  if (o.taxTotal != null && o.taxTotal !== "" && !isNaN(Number(o.taxTotal))) {
    return Math.round(Number(o.taxTotal) * 100) / 100;
  }
  if (o.tax != null && o.tax !== "" && !isNaN(Number(o.tax))) {
    return Math.round(Number(o.tax) * 100) / 100;
  }
  if (Array.isArray(o.items) && o.items.length > 0) {
    const total = o.items.reduce((sum, it) => {
      if (it.taxAmount != null && it.taxAmount !== "" && !isNaN(Number(it.taxAmount))) {
        return sum + Number(it.taxAmount);
      }
      const rate = Number(it.taxRate != null && it.taxRate !== "" ? it.taxRate : (it.gstRate || 0));
      if (rate > 0) {
        const lineBase = Number(it.price || 0) * Number(it.quantity || 1);
        return sum + (lineBase * rate) / 100;
      }
      return sum;
    }, 0);
    return Math.round(total * 100) / 100;
  }
  return 0;
};

export const orderGrandTotal = (o, products = []) => {
  if (!o) return 0;
  const sub = orderSubtotal(o);
  const tax = orderTaxTotal(o);
  const ship = invoiceShipping(o, products);
  const extra = orderExtraCharge(o);
  const cod = orderCodCharge(o);
  const disc = orderDiscount(o);
  const computed = Math.max(0, Math.round(sub + tax + ship + extra + cod - disc));
  if (computed > 0) return computed;
  if (o.total != null && o.total !== "" && !isNaN(Number(o.total)) && Number(o.total) > 0) {
    return Math.round(Number(o.total));
  }
  return 0;
};

export const orderAdvanceAmount = (o, products = []) => {
  if (!o) return 0;
  if (o.advancePaid != null && o.advancePaid !== "" && !isNaN(Number(o.advancePaid))) {
    return Number(o.advancePaid);
  }
  return 0;
};

export const orderBalanceDue = (o, products = []) => {
  if (!o) return 0;
  const isCod =
    Number(o.codCharge || 0) > 0 ||
    o.paymentType === "COD" ||
    (typeof o.paymentMethod === "string" && o.paymentMethod.toUpperCase().includes("COD"));
  if (isCod) {
    const total = orderGrandTotal(o, products);
    const adv = orderAdvanceAmount(o, products);
    return Math.max(0, total - adv);
  }
  return 0;
};

export const orderPurchaseCost = (o, products = []) => {
  if (!o) return 0;
  if (o.purchaseCost != null && o.purchaseCost !== "") {
    return Number(o.purchaseCost);
  }
  return (o.items || []).reduce((itemSum, it) => {
    if (it.purchaseCost != null && it.purchaseCost !== "" && !isNaN(Number(it.purchaseCost))) {
      const addCost = Number(it.additionalCost || 0);
      return itemSum + (Number(it.purchaseCost) + addCost) * Number(it.quantity || 1);
    }
    const prod = Array.isArray(products) ? products.find((p) => p.id === it.id) : null;
    const fixedProductCost = prod?.productCost != null && prod.productCost !== ""
      ? Number(prod.productCost)
      : (prod?.costPrice != null ? Number(prod.costPrice) : Math.round(Number(it.price || 0) * 0.45));
    const fixedPackagingCost = prod?.packagingCost != null && prod.packagingCost !== ""
      ? Number(prod.packagingCost)
      : 80;
    return itemSum + (fixedProductCost + fixedPackagingCost) * Number(it.quantity || 1);
  }, 0);
};

export const calculateSingleOrderProfit = (o, products = []) => {
  if (!o) return 0;
  if (o.profit != null && o.profit !== "" && !isNaN(Number(o.profit))) {
    return Number(o.profit);
  }
  const sub = orderSubtotal(o);
  const tax = orderTaxTotal(o);
  const cogs = orderPurchaseCost(o, products);
  const disc = orderDiscount(o);
  const extra = orderExtraCharge(o);

  // When delivery is excluded (deliveryIncluded === false), shipping is charged to the customer
  // on top of the bill, fully covering the courier cost.
  // Profit = subtotal + tax - purchase cost (+ extra - disc)
  // When delivery is included in price (deliveryIncluded !== false, free shipping), the seller
  // absorbs the courier shipping cost:
  // Profit = subtotal + tax - purchase cost - shipping (+ extra - disc)
  const isDeliveryIncluded = o.deliveryIncluded !== false;
  const sellerAbsorbedShipping = isDeliveryIncluded ? orderShipping(o, products) : 0;

  return Math.round(sub + tax + extra - disc - cogs - sellerAbsorbedShipping);
};

export const calculateItemTax = (price, quantity = 1, gstRate = 0, isInclusive = true) => {
  const numPrice = Number(price || 0);
  const qty = Number(quantity || 1);
  const rate = Number(gstRate != null && gstRate !== "" ? gstRate : 0);
  if (rate <= 0) {
    const total = Number((numPrice * qty).toFixed(2));
    return {
      totalInclTax: total,
      exclTax: total,
      taxAmount: 0,
      gstRate: 0,
      isInclusive,
    };
  }
  if (isInclusive) {
    const totalInclTax = Number((numPrice * qty).toFixed(2));
    const exclTax = Number((totalInclTax / (1 + rate / 100)).toFixed(2));
    const taxAmount = Number((totalInclTax - exclTax).toFixed(2));
    return {
      totalInclTax,
      exclTax,
      taxAmount,
      gstRate: rate,
      isInclusive: true,
    };
  } else {
    const exclTax = Number((numPrice * qty).toFixed(2));
    const taxAmount = Number(((exclTax * rate) / 100).toFixed(2));
    const totalInclTax = Number((exclTax + taxAmount).toFixed(2));
    return {
      totalInclTax,
      exclTax,
      taxAmount,
      gstRate: rate,
      isInclusive: false,
    };
  }
};

/**
 * Recalculates all order financials when items or customer address changes.
 * Returns updated patch for items, subtotal, grossSubtotal, tax, cgst, sgst, igst, total, and codBalanceDue.
 */
export const recalculateOrderFinancials = (order, newItems, products = []) => {
  const isGst =
    order.isGst !== false &&
    order.isNonGst !== true &&
    order.nonGst !== true &&
    order.gstType !== "non-gst" &&
    order.gstMode !== "none";
  const isExclusive = isGst && order.gstMode === "exclusive";
  const isIntra = isWestBengalState(order.state || "West Bengal");

  const items = (newItems || []).map((it) => {
    const qty = Math.max(1, Number(it.quantity || 1));
    const price = Math.max(0, Number(it.price || 0));
    const rate = isGst ? Math.max(0, Number(it.taxRate != null ? it.taxRate : (it.gstRate || 0))) : 0;
    const taxCalc = calculateItemTax(price, qty, rate, !isExclusive);
    return {
      ...it,
      quantity: qty,
      price: price,
      taxRate: rate,
      gstRate: rate,
      baseUnitPrice: isExclusive ? price : Number((taxCalc.exclTax / qty).toFixed(2)),
      lineBase: taxCalc.exclTax,
      taxAmount: taxCalc.taxAmount,
      total: taxCalc.totalInclTax,
    };
  });

  const taxableSubtotal = items.reduce((s, it) => s + (it.lineBase || 0), 0);
  const totalTax = isGst ? items.reduce((s, it) => s + (it.taxAmount || 0), 0) : 0;
  const roundedTax = Number(totalTax.toFixed(2));
  const grossItems = items.reduce((s, it) => s + (it.total || 0), 0);

  const appliedCgst = isGst && isIntra ? Number((roundedTax / 2).toFixed(2)) : 0;
  const appliedSgst = isGst && isIntra ? Number((roundedTax - appliedCgst).toFixed(2)) : 0;
  const appliedIgst = isGst && !isIntra ? roundedTax : 0;

  const mockOrder = {
    ...order,
    items,
    subtotal: isGst && isExclusive ? Number(taxableSubtotal.toFixed(2)) : Number(grossItems.toFixed(2)),
    grossSubtotal: Number(grossItems.toFixed(2)),
    tax: roundedTax,
    taxTotal: roundedTax,
    cgst: appliedCgst,
    sgst: appliedSgst,
    igst: appliedIgst,
  };

  const grandTotal = orderGrandTotal(mockOrder, products);
  const balanceDue = orderBalanceDue({ ...mockOrder, total: grandTotal }, products);

  return {
    items,
    subtotal: isGst && isExclusive ? Number(taxableSubtotal.toFixed(2)) : Number(grossItems.toFixed(2)),
    grossSubtotal: Number(grossItems.toFixed(2)),
    tax: roundedTax,
    taxTotal: roundedTax,
    cgst: appliedCgst,
    sgst: appliedSgst,
    igst: appliedIgst,
    total: grandTotal,
    codBalanceDue: balanceDue,
  };
};

export const getInvoiceNo = (order) => {
  if (!order) return "";
  if (order.invoiceNo) return order.invoiceNo;
  const rawDate = order.createdAt?.seconds ? order.createdAt.seconds * 1000 : order.createdAt;
  let created = rawDate ? new Date(rawDate) : new Date();
  if (isNaN(created.getTime())) {
    created = new Date();
  }
  const year = created.getFullYear();
  const month = String(created.getMonth() + 1).padStart(2, "0");
  const day = String(created.getDate()).padStart(2, "0");
  const shortId = String(order.id || "").toUpperCase();
  return `LC-${year}${month}${day}-${shortId}`;
};

export const CARRIER_OPTIONS = ["Others", "India Post", "Shiprocket", "Public Transport"];

export const INDIAN_STATES = [
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
];

export const isWestBengalState = (state = "") => {
  const s = String(state || "").toLowerCase().trim();
  return s === "west bengal" || s === "wb" || s.startsWith("west bengal");
};

export const getCarrierType = (courierName = "") => {
  if (!courierName) return "Others";
  const c = courierName.toLowerCase().trim();
  if (c === "others" || c === "other") return "Others";
  if (c.includes("shiprocket")) return "Shiprocket";
  if (c.includes("transport") || c.includes("public")) return "Public Transport";
  if (c.includes("post") || c.includes("india")) return "India Post";
  if (CARRIER_OPTIONS.includes(courierName)) return courierName;
  return "Others";
};

export const getCarrierTrackingUrl = (courierName = "", cons = "") => {
  const carrier = getCarrierType(courierName);
  const cleanCons = encodeURIComponent((cons || "").trim());
  if (carrier === "Shiprocket") {
    return cleanCons
      ? `https://shiprocket.co/tracking/${cleanCons}`
      : "https://www.shiprocket.in/shipment-tracking/";
  }
  if (carrier === "India Post") {
    return "https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx";
  }
  if (cleanCons) {
    const query = encodeURIComponent(`${courierName && courierName !== "Others" && courierName !== "Other" ? courierName : "courier"} tracking ${cons.trim()}`);
    return `https://www.google.com/search?q=${query}`;
  }
  return "";
};

export const STATUS_CONFIG = {
  New: {
    badge: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/60 dark:text-blue-200 dark:border-blue-700 font-semibold",
    activeBtn: "bg-blue-600 text-white border-blue-600 shadow-xs font-semibold ring-2 ring-blue-400/50",
    idleBtn: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-900/60 font-medium",
  },
  Confirmed: {
    badge: "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/60 dark:text-purple-200 dark:border-purple-700 font-semibold",
    activeBtn: "bg-purple-600 text-white border-purple-600 shadow-xs font-semibold ring-2 ring-purple-400/50",
    idleBtn: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800 dark:hover:bg-purple-900/60 font-medium",
  },
  Shipped: {
    badge: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/60 dark:text-amber-200 dark:border-amber-700 font-semibold",
    activeBtn: "bg-amber-600 text-white border-amber-600 shadow-xs font-semibold ring-2 ring-amber-400/50",
    idleBtn: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-900/60 font-medium",
  },
  Delivered: {
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/60 dark:text-emerald-200 dark:border-emerald-700 font-semibold",
    activeBtn: "bg-emerald-600 text-white border-emerald-600 shadow-xs font-semibold ring-2 ring-emerald-400/50",
    idleBtn: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/60 font-medium",
  },
  Cancelled: {
    badge: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/60 dark:text-rose-200 dark:border-rose-700 font-semibold",
    activeBtn: "bg-rose-600 text-white border-rose-600 shadow-xs font-semibold ring-2 ring-rose-400/50",
    idleBtn: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/60 font-medium",
  },
};

/**
 * Converts a numerical currency amount to formal Indian currency words.
 * Handles Crores, Lakhs, Thousands, Hundreds, and Paise.
 * Example: 1250 -> "One Thousand Two Hundred Fifty Rupees Only"
 */
export function numberToIndianWords(amount) {
  const rounded = Math.round(Number(amount) || 0);
  if (rounded <= 0) return "Zero Rupees Only";

  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function convertTwoDigits(n) {
    if (n < 20) return ones[n];
    const t = tens[Math.floor(n / 10)];
    const u = ones[n % 10];
    return t + (u ? " " + u : "");
  }

  function convertThreeDigits(n) {
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    let res = "";
    if (hundred > 0) {
      res += ones[hundred] + " Hundred";
      if (rest > 0) res += " ";
    }
    if (rest > 0) {
      res += convertTwoDigits(rest);
    }
    return res;
  }

  let crore = Math.floor(rounded / 10000000);
  let remainder = rounded % 10000000;

  let lakh = Math.floor(remainder / 100000);
  remainder = remainder % 100000;

  let thousand = Math.floor(remainder / 1000);
  remainder = remainder % 1000;

  let words = "";

  if (crore > 0) {
    words += convertTwoDigits(crore) + " Crore ";
  }
  if (lakh > 0) {
    words += convertTwoDigits(lakh) + " Lakh ";
  }
  if (thousand > 0) {
    words += convertTwoDigits(thousand) + " Thousand ";
  }
  if (remainder > 0) {
    words += convertThreeDigits(remainder) + " ";
  }

  return (words.trim() + " Rupees Only").replace(/\s+/g, " ");
}



